import { findColumn } from '@blaze-shared/schema-domain';
import {
  type BaseSchema,
  type BlazeSchema,
  type WorkflowSchema,
} from '@blaze-shared/schema-domain';

/**
 * Merges a node's input with data fetched alongside it — an API-call response, a looked-up row.
 *
 * Either side may be a single row or a list, so the result follows the wider one: two lists merge
 * pairwise by position, a list against a row merges every row against it, and a row against an
 * empty list stays as it is.
 */
export function mergeData(
  dataA: any,
  dataB: any,
  dataASchema: WorkflowSchema,
  dataBSchema?: BaseSchema,
  name?: string,
  mergeSymbol = '.',
): any {
  if (Array.isArray(dataA) && Array.isArray(dataB)) {
    const merged: any[] = [];
    const length = Math.max(dataA.length, dataB.length);
    for (let i = 0; i < length; i++) {
      const rowA = dataA[i] ?? {};
      const rowB = dataB[i] ?? {};
      merged.push(mergeObjects(rowA, rowB, dataASchema, dataBSchema, name, mergeSymbol));
    }
    return merged;
  }
  if (Array.isArray(dataA) && !Array.isArray(dataB)) {
    return dataA.map((rowA) =>
      mergeObjects(rowA, dataB, dataASchema, dataBSchema, name, mergeSymbol),
    );
  }
  if (!Array.isArray(dataA) && Array.isArray(dataB)) {
    const merged = dataB.map((rowB) =>
      mergeObjects(dataA, rowB, dataASchema, dataBSchema, name, mergeSymbol),
    );
    if (!dataB?.length) {
      merged.push(dataA);
    }
    return merged;
  }
  return mergeObjects(dataA, dataB, dataASchema, dataBSchema, name, mergeSymbol);
}

/**
 * Merges two rows, keeping both values of any column they share.
 *
 * A column present on both sides would otherwise lose one of its values, so B's copy is kept under
 * a qualified name — `Orders.Name` for a `Name` that both rows carry.
 */
export function mergeObjects(
  objA: any,
  objB: any,
  objASchema: WorkflowSchema,
  objBSchema?: BaseSchema,
  name?: string,
  mergeSymbol = '.',
): any {
  const merged: { [key: string]: any } = {};
  for (const key in objA) {
    if (Object.prototype.hasOwnProperty.call(objA, key)) {
      if (hasColumn(objA, key, objASchema) && hasColumn(objB, key, objBSchema)) {
        merged[`${key}`] = objA[key];
        const prefix = objBSchema?.name || name;
        merged[`${prefix}${mergeSymbol}${key}`] = objB[key];
      } else {
        merged[key] = objA[key];
      }
    }
  }
  // Duplicates are already placed, so B contributes only the columns A does not have.
  for (const key in objB) {
    if (Object.prototype.hasOwnProperty.call(objB, key)) {
      if (!hasColumn(objA, key, objASchema)) {
        merged[key] = objB[key];
      }
    }
  }
  return merged;
}

/**
 * Whether a row carries a column — by its schema when one is known, by the row's own keys
 * otherwise, since a row can carry columns its schema does not declare.
 */
export function hasColumn(row: any = {}, col: string, schema?: BlazeSchema): boolean {
  if (!col) {
    return false;
  }
  if (schema && !!findColumn(schema?.columns, col)) {
    return true;
  }
  return row?.[col] !== undefined;
}
